"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const HandledError_1 = require("./HandledError");
const WidgetEvents_1 = require("../Widget/EventDefinition/WidgetEvents");
const WidgetError_1 = require("./WidgetError");
/**
 * Service for handling errors.
 */
class ErrorHandler {
    /**
     * Error handler constructor.
     *
     * @param {ErrorHandlerPluginManager} errorHandlerPluginManager
     */
    constructor(errorHandlerPluginManager) {
        /**
         * The last error handle time.
         */
        this.lastErrorTime = 0;
        /**
         * The amount of short period errors to tolerate.
         * More than this should mean infinite loop.
         */
        this.handledThrottles = 0;
        /**
         * Whether we are already handling an error.
         * @type {boolean}
         */
        this.isHandlingInProgress = false;
        this.errorHandlerPluginManager = errorHandlerPluginManager;
    }
    /**
     * Attempts to handle an error generated in the lifetime of widget.
     *
     * @param {*} error
     *   The error that occurred.
     * @param {WidgetController} widget
     *   The widget instance for which the error occurred.
     *
     * @return {Promise<boolean>}
     *   Should return whether the error was handled.
     *   When TRUE all consecutive handlers will be ignored.
     */
    handle(error, widget) {
        // We never handle handled errors so that for certain errors the widget
        // can bypass the error handling flows.
        if (HandledError_1.default.is(error)) {
            // Make sure we emmit the original error.
            this.notifyWidget(error.getActualError(), widget, true);
            return Promise.resolve(true);
        }
        if (this.isHandlingInProgress === true) {
            this.notifyWidget(error, widget, false);
            return Promise.resolve(false);
        }
        if (this.throttle()) {
            console.error(error);
            return Promise.reject(new HandledError_1.default(error));
        }
        const handlersDefinitions = this.errorHandlerPluginManager.getDefinitions();
        this.isHandlingInProgress = true;
        return this.doHandle(handlersDefinitions, error, widget)
            .then((handled) => {
            this.notifyWidget(error, widget, handled);
            this.isHandlingInProgress = false;
            return handled;
        });
    }
    /**
     * Does the iterative promise handling of error plugins.
     *
     * @param {ErrorHandlerPluginDefinition[]} definitions
     * @param error
     * @param {WidgetController} widget
     * @return {Promise<boolean>}
     */
    doHandle(definitions, error, widget) {
        if (!definitions.length) {
            return Promise.resolve(false);
        }
        const errorHandlerId = definitions.shift().getId();
        return this.errorHandlerPluginManager.create(errorHandlerId)
            .handle(error, widget)
            .catch(() => {
            // Errors that occur in handlers cannot be handled.
            console.error(new WidgetError_1.default(`CRITICAL: Error handler '${errorHandlerId}' generated error.`));
            // Return false. Maybe other handlers can respond to it.
            return false;
        })
            .then((handled) => {
            if (handled) {
                return true;
            }
            // If the error was not handling we go to next handler.
            return this.doHandle(definitions, error, widget);
        });
    }
    /**
     * Checks whether error handling throttle should be enabled.
     * @return {boolean}
     */
    throttle() {
        const currentTime = Date.now();
        if (currentTime - this.lastErrorTime < this.constructor.MIN_HANDLE_TIME_DIFF) {
            ++this.handledThrottles;
        }
        else {
            this.handledThrottles = 0;
        }
        this.lastErrorTime = currentTime;
        if (this.handledThrottles > this.constructor.MAX_THROTTLE_TOLERATE) {
            this.handledThrottles = 0;
            console.error(new WidgetError_1.default(`CRITICAL: Infinite error handling loop detected !!`));
            return true;
        }
        return false;
    }
    /**
     * Emits the error on the widget event container.
     *
     * @param error
     * @param {WidgetController} widget
     * @param {boolean} wasHandled
     */
    notifyWidget(error, widget, wasHandled) {
        if (wasHandled) {
            widget.getEventContainer().emit(WidgetEvents_1.WidgetEvents.HANDLED_ERROR, error);
        }
        else {
            try {
                widget.getEventContainer().emit(WidgetEvents_1.WidgetEvents.ERROR, error);
            }
            catch (error) {
                // There might be no listeners. In that case the error get s thrown.
                // There is nothing else left to do but to log it.
                console.error(error);
            }
        }
    }
    /**
     * Creates promise callback for handling errors.
     *
     * @param {WidgetController} widget
     * @return {(err: any) => any}
     */
    handlePromised(widget) {
        return (error) => {
            return this.handle(error, widget)
                .then(() => {
                throw error;
            });
        };
    }
}
/**
 * The minimum time tolerated for handling consecutive errors.
 * @type {number}
 */
ErrorHandler.MIN_HANDLE_TIME_DIFF = 10;
/**
 * The amount of short period errors to tolerate.
 * @type {number}
 */
ErrorHandler.MAX_THROTTLE_TOLERATE = 100;
exports.default = ErrorHandler;
